import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Drawer from '@material-ui/core/Drawer';
import IconButton from '@material-ui/core/IconButton';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CloseIcon from '@material-ui/icons/Close';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import Layout from '../../Components/layouts';
import GenericTable from '../../Components/GenericTable';
import SearchBoxInput from '../../Components/SearchBoxInput';
import StatusChip, { toneForStatus } from '../../Components/StatusChip';
import { useAuthState } from '../../Context';
import { ProjectServices } from '../../Services/ProjectServices';
import { SkillsAndTransformation } from '../../Services/SkillsAndTransformation';
import commonStyles from '../../theme/commonStyles';
import { notify } from '../../utils';
import { USER_ROLES } from '../../utils/constants';
import {
  APPROVE_COPY,
  APPROVE_SUCCESS,
  DECLINE_COPY,
  DECLINE_SUCCESS,
  NO_COURSES_SELECTED,
  approveBlockedMessage,
  canApproveLearningHubRequest,
  filterRequests,
  isReviewSuccess,
  learningAccessFromResponse,
  pendingRequestCount,
  projectNameFor,
  projectsFromResponse,
  requestsFromResponse,
  reviewPayload,
  selectedCourseSummary,
} from './requestInbox';

const skillsAndTransformation = new SkillsAndTransformation();
const projectServices = new ProjectServices();

const useStyles = makeStyles((theme) => ({
  intro: {
    color: 'var(--dm-muted, #647184)',
    margin: theme.spacing(0, 0, 2),
    maxWidth: 720,
  },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  search: {
    flex: '1 1 240px',
    minWidth: 0,
  },
  statusField: {
    flex: '1 1 180px',
    maxWidth: 220,
    minWidth: 160,
  },
  pendingCount: {
    fontWeight: 700,
    color: 'var(--dm-text-strong, #263548)',
  },
  rowActions: {
    display: 'inline-flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    whiteSpace: 'nowrap',
  },
  viewBtn: {
    width: 34,
    height: 34,
    padding: '8px !important',
    borderRadius: 8,
    color: `${theme.palette.primary.main} !important`,
    backgroundColor: 'rgba(61, 125, 221, 0.1) !important',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: 'rgba(61, 125, 221, 0.18) !important',
    },
  },
  approveBtn: {
    width: 34,
    height: 34,
    marginLeft: 8,
    padding: '8px !important',
    borderRadius: 8,
    color: '#1f7a3f !important',
    backgroundColor: 'rgba(40, 167, 69, 0.12) !important',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: 'rgba(40, 167, 69, 0.2) !important',
    },
  },
  declineBtn: {
    width: 34,
    height: 34,
    marginLeft: 8,
    padding: '8px !important',
    borderRadius: 8,
    color: '#c62828 !important',
    backgroundColor: 'rgba(245, 25, 25, 0.1) !important',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: 'rgba(245, 25, 25, 0.18) !important',
    },
  },
  actionBtn: {
    textTransform: 'none',
    fontWeight: 650,
    borderRadius: 8,
  },
  blockedBtn: {
    opacity: 0.55,
  },
  stateMessage: {
    padding: theme.spacing(5, 2),
    textAlign: 'center',
    color: 'var(--dm-muted, #647184)',
  },
  errorMessage: {
    color: '#b42318',
    marginBottom: theme.spacing(2),
  },
  drawerPaper: {
    width: 440,
    maxWidth: '100%',
    boxSizing: 'border-box',
    padding: theme.spacing(2.5),
    backgroundColor: 'var(--contentColor, #fff)',
    color: 'var(--dm-text-strong, #263548)',
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  meta: {
    color: 'var(--dm-muted, #647184)',
    marginTop: 4,
  },
  section: {
    marginTop: theme.spacing(2.5),
  },
  courseList: {
    margin: theme.spacing(1, 0, 0),
    paddingLeft: theme.spacing(2.5),
  },
  courseMeta: {
    color: 'var(--dm-muted, #647184)',
    fontSize: '0.8125rem',
  },
  seatBox: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderRadius: 8,
    border: '1px solid var(--dm-border, #e7ebf0)',
    backgroundColor: 'var(--dm-row-alt, #f8fafc)',
  },
  drawerActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  blockReason: {
    color: '#9a5410',
    marginTop: theme.spacing(1),
  },
}));

const statusLabel = (status) => {
  const value = typeof status === 'string' ? status : '';
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const formatRequestDate = (value) => {
  if (!value) return '';
  const parsed = moment(value);
  if (!parsed.isValid()) return String(value);
  const hasTime = typeof value === 'string' && value.includes('T') && (
    parsed.hour() !== 0 || parsed.minute() !== 0 || parsed.second() !== 0
  );
  return parsed.format(hasTime ? 'D MMM YYYY, HH:mm' : 'D MMM YYYY');
};

const customerIdFromSession = () => {
  const customerId = localStorage.getItem('company_id');
  return customerId && customerId !== 'null' ? customerId : '';
};

const LearningHubRequests = () => {
  const classes = useStyles();
  const commonClasses = commonStyles();
  const userDetails = useAuthState();
  const impersonation = { active: false };
  const role = userDetails?.role || localStorage.getItem('role') || '';
  const canReview = !impersonation.active && role && role !== USER_ROLES.USER;

  const [requests, setRequests] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [seatCache, setSeatCache] = useState({});
  const [seatsLoadingId, setSeatsLoadingId] = useState('');
  const [seatError, setSeatError] = useState('');
  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState('');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const seatCacheRef = useRef({});

  const loadSeats = async (projectId, force = false) => {
    if (!projectId) return null;
    if (!force && seatCacheRef.current[projectId]) return seatCacheRef.current[projectId];
    setSeatsLoadingId(projectId);
    setSeatError('');
    const response = await projectServices.getLearningAccess(projectId);
    const access = learningAccessFromResponse(response);
    if (access) {
      seatCacheRef.current = { ...seatCacheRef.current, [projectId]: access };
      setSeatCache(seatCacheRef.current);
    } else {
      setSeatError(response?.message || 'Seat availability could not be loaded.');
    }
    setSeatsLoadingId('');
    return access;
  };

  const loadInbox = async () => {
    setLoading(true);
    const customerId = customerIdFromSession();
    const [requestResponse, projectResponse] = await Promise.all([
      skillsAndTransformation.getLearningHubRequests(),
      customerId
        ? projectServices.allProjectsByCustomer(customerId).catch(() => null)
        : Promise.resolve(null),
    ]);
    const parsed = requestsFromResponse(requestResponse);
    setRequests(parsed.rows);
    setSelected((current) => (
      current ? parsed.rows.find((row) => row.uuid === current.uuid) || current : current
    ));
    setError(parsed.error);
    setProjects(projectsFromResponse(projectResponse));
    setLoading(false);
  };

  useEffect(() => {
    if (!canReview) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    loadInbox().then(() => {
      if (cancelled) return undefined;
      return undefined;
    });
    return () => {
      cancelled = true;
    };
    // The inbox loads once for the signed-in reviewer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canReview]);

  useEffect(() => {
    if (!drawerOpen || !selected?.project_id) return undefined;
    let cancelled = false;
    loadSeats(selected.project_id).then(() => {
      if (cancelled) return undefined;
      return undefined;
    });
    return () => {
      cancelled = true;
    };
    // Reload seats when a different request is opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpen, selected?.uuid]);

  useEffect(() => {
    if (!canReview || loading) return undefined;
    const projectIds = [...new Set(
      requests
        .filter((row) => row && row.status === 'pending' && row.project_id)
        .map((row) => row.project_id)
    )];
    projectIds.forEach((projectId) => {
      if (!seatCacheRef.current[projectId]) loadSeats(projectId);
    });
    return undefined;
    // Seat reads stay read-only and are cached per project.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canReview, loading, requests]);

  const visibleRequests = filterRequests(requests, {
    status: statusFilter,
    search,
    projects,
  });
  const pendingCount = pendingRequestCount(requests);
  const selectedAccess = selected ? seatCache[selected.project_id] : null;
  const selectedBlocked = selected ? approveBlockedMessage(selectedAccess) : '';
  const seatsLoading = Boolean(selected && !selectedAccess && seatsLoadingId === selected.project_id);

  const openDetails = (row) => {
    setSelected(row);
    setSeatError('');
    setDrawerOpen(true);
  };

  const startApprove = (row) => {
    setSelected(row);
    setSeatError('');
    setComment('');
    setConfirmAction('approved');
    loadSeats(row.project_id);
  };

  const startDecline = (row) => {
    setSelected(row);
    setComment('');
    setConfirmAction('declined');
  };

  const closeConfirm = () => {
    if (submitting) return;
    setConfirmAction('');
    setComment('');
  };

  const submitReview = async () => {
    if (!selected || !confirmAction || submitting) return;
    if (confirmAction === 'approved' && !canApproveLearningHubRequest(selectedAccess)) {
      notify('error', approveBlockedMessage(selectedAccess));
      return;
    }
    setSubmitting(true);
    const response = await skillsAndTransformation.reviewLearningHubRequest(
      selected.uuid,
      reviewPayload(confirmAction, comment)
    );
    setSubmitting(false);
    if (!isReviewSuccess(response)) {
      notify('error', response?.message || 'Unable to update the Learning Hub request');
      return;
    }
    notify('success', confirmAction === 'approved' ? APPROVE_SUCCESS : DECLINE_SUCCESS);
    const projectId = selected.project_id;
    setConfirmAction('');
    setComment('');
    delete seatCacheRef.current[projectId];
    setSeatCache({ ...seatCacheRef.current });
    await Promise.all([
      loadInbox(),
      loadSeats(projectId, true),
    ]);
  };

  const columns = [
    {
      label: 'Learner',
      key: 'user_email',
      render: (row) => row.user_email || '',
    },
    {
      label: 'Project',
      key: 'project',
      render: (row) => projectNameFor(row.project_id, projects),
    },
    {
      label: 'Selected courses',
      key: 'courses',
      render: (row) => selectedCourseSummary(row),
    },
    {
      label: 'Comment',
      key: 'comment',
      render: (row) => row.comment || '—',
    },
    {
      label: 'Requested',
      key: 'created_at',
      render: (row) => formatRequestDate(row.created_at) || '',
    },
    {
      label: 'Status',
      key: 'statusLabel',
      render: (row) => (
        <StatusChip tone={toneForStatus(row.status)}>{statusLabel(row.status)}</StatusChip>
      ),
    },
    {
      label: 'Action',
      key: 'actions',
      render: (row) => {
        const email = row.user_email || 'learner';
        const pending = row.status === 'pending';
        const blocked = Boolean(seatCache[row.project_id] && !canApproveLearningHubRequest(seatCache[row.project_id]));
        return (
          <div className={classes.rowActions}>
            <IconButton
              size="small"
              className={classes.viewBtn}
              onClick={() => openDetails(row)}
              aria-label={`View request from ${email}`}
            >
              <VisibilityOutlinedIcon fontSize="small" />
            </IconButton>
            {pending ? (
              <IconButton
                size="small"
                className={`${classes.approveBtn}${blocked ? ` ${classes.blockedBtn}` : ''}`}
                aria-disabled={blocked}
                aria-label={blocked ? `Approve unavailable. ${approveBlockedMessage(seatCache[row.project_id])}` : `Approve request from ${email}`}
                onClick={() => startApprove(row)}
              >
                <CheckCircleIcon fontSize="small" />
              </IconButton>
            ) : null}
            {pending ? (
              <IconButton
                size="small"
                className={classes.declineBtn}
                onClick={() => startDecline(row)}
                aria-label={`Decline request from ${email}`}
              >
                <CancelIcon fontSize="small" />
              </IconButton>
            ) : null}
          </div>
        );
      },
    },
  ];

  const courses = Array.isArray(selected?.selected_courses) ? selected.selected_courses : [];
  const summary = selectedAccess?.seat_summary || {};
  const allocations = Array.isArray(selectedAccess?.allocations) ? selectedAccess.allocations : [];

  return (
    <Layout>
      <main>
        <div className={commonClasses.contentWrapper} data-testid="learning-hub-requests">
          <Typography variant="h4" className={commonClasses.pageTitle}>Learning Hub requests</Typography>
          {!canReview ? (
            <Typography className={classes.stateMessage} role="status">
              Learning Hub upgrade requests are reviewed by a Customer Admin or Project Admin.
            </Typography>
          ) : (
            <>
              <Typography className={classes.intro}>
                One request covers one learner and one seat. Approving unlocks every Learning Hub course on that project. The learner still enrolls themselves.
              </Typography>
              <div className={classes.toolbar}>
                <div className={classes.search}>
                  <SearchBoxInput
                    label="Search requests"
                    placeholder="Email, project, or course"
                    value={search}
                    styleOverrides={{ minWidth: 0, width: '100%' }}
                    handleChange={(event) => setSearch(event.target.value)}
                    handleSearch={(event) => event.preventDefault()}
                  />
                </div>
                <TextField
                  className={classes.statusField}
                  select
                  size="small"
                  variant="outlined"
                  label="Status"
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="declined">Declined</MenuItem>
                </TextField>
                {!loading ? (
                  <Typography className={classes.pendingCount} aria-live="polite">
                    {pendingCount} pending
                  </Typography>
                ) : null}
              </div>
              {error ? (
                <Typography className={classes.errorMessage} role="alert">{error}</Typography>
              ) : null}
              {loading || visibleRequests.length ? (
                <GenericTable columns={columns} list={visibleRequests} loading={loading} left applyMinWidth={false} />
              ) : error ? null : (
                <Typography className={classes.stateMessage} role="status">
                  {requests.length ? 'No matching upgrade requests' : 'No Learning Hub upgrade requests'}
                </Typography>
              )}
            </>
          )}
        </div>
      </main>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        classes={{ paper: classes.drawerPaper }}
        aria-labelledby="learning-hub-request-title"
      >
        {selected ? (
          <div>
            <div className={classes.drawerHeader}>
              <div>
                <Typography id="learning-hub-request-title" variant="h6">
                  {selected.user_email || 'Learner'}
                </Typography>
                <Typography className={classes.meta}>
                  {projectNameFor(selected.project_id, projects)}
                </Typography>
              </div>
              <IconButton aria-label="Close request details" onClick={() => setDrawerOpen(false)}>
                <CloseIcon />
              </IconButton>
            </div>
            <div className={classes.section}>
              <StatusChip tone={toneForStatus(selected.status)}>{statusLabel(selected.status)}</StatusChip>
              <Typography className={classes.meta}>
                Requested {formatRequestDate(selected.created_at) || '—'}
              </Typography>
              {selected.comment ? (
                <Typography className={classes.meta}>{selected.comment}</Typography>
              ) : null}
            </div>
            <div className={classes.section}>
              <Typography variant="subtitle1">Selected courses</Typography>
              {courses.length ? (
                <ul className={classes.courseList}>
                  {courses.map((course) => (
                    <li key={course.uuid || course.name}>
                      <Typography>{course.name || 'Untitled course'}</Typography>
                      <Typography className={classes.courseMeta}>
                        {[course.category, course.provider].filter(Boolean).join(' · ')}
                      </Typography>
                    </li>
                  ))}
                </ul>
              ) : (
                <Typography className={classes.meta}>{NO_COURSES_SELECTED}</Typography>
              )}
            </div>
            <div className={classes.section}>
              <Typography variant="subtitle1">Seats</Typography>
              {seatsLoading ? <CircularProgress size={22} aria-label="Loading seats" /> : null}
              {selectedAccess ? (
                <div className={classes.seatBox}>
                  {selectedAccess.seat_message ? (
                    <Typography>{selectedAccess.seat_message}</Typography>
                  ) : null}
                  <Typography className={classes.meta}>
                    {`Allocated ${summary.allocated ?? '—'} · Utilized ${summary.utilized ?? '—'} · Available ${summary.available ?? '—'}`}
                  </Typography>
                  {allocations.map((allocation) => (
                    <Typography
                      key={allocation.uuid || `${allocation.status}-${allocation.seats_remaining}`}
                      className={classes.courseMeta}
                    >
                      {`${statusLabel(allocation.status) || 'Allocation'}: ${allocation.seats_remaining ?? '—'} remaining`}
                    </Typography>
                  ))}
                </div>
              ) : null}
              {seatError && !seatsLoading ? (
                <Typography className={classes.blockReason} role="alert">{seatError}</Typography>
              ) : null}
            </div>
            {selected.status === 'pending' ? (
              <div className={classes.drawerActions}>
                <Button
                  color="primary"
                  variant="contained"
                  className={`${classes.actionBtn}${selectedBlocked ? ` ${classes.blockedBtn}` : ''}`}
                  aria-disabled={Boolean(selectedBlocked) || seatsLoading}
                  aria-label={selectedBlocked ? `Approve unavailable. ${selectedBlocked}` : `Approve request from ${selected.user_email || 'learner'}`}
                  onClick={() => startApprove(selected)}
                >
                  Approve
                </Button>
                <Button
                  variant="outlined"
                  className={classes.actionBtn}
                  onClick={() => startDecline(selected)}
                  aria-label={`Decline request from ${selected.user_email || 'learner'}`}
                >
                  Decline
                </Button>
              </div>
            ) : null}
            {selected.status === 'pending' && selectedBlocked && !seatsLoading ? (
              <Typography className={classes.blockReason}>{selectedBlocked}</Typography>
            ) : null}
          </div>
        ) : null}
      </Drawer>
      <Dialog
        open={Boolean(confirmAction)}
        onClose={closeConfirm}
        fullWidth
        maxWidth="sm"
        aria-labelledby="learning-hub-review-title"
      >
        <DialogTitle id="learning-hub-review-title">
          {confirmAction === 'approved' ? 'Approve Learning Hub access' : 'Decline Learning Hub request'}
        </DialogTitle>
        <DialogContent dividers>
          <Typography>
            {confirmAction === 'approved' ? APPROVE_COPY : DECLINE_COPY}
          </Typography>
          {selected ? (
            <Typography className={classes.meta}>{selected.user_email}</Typography>
          ) : null}
          {confirmAction === 'approved' && seatsLoading ? (
            <Typography className={classes.meta}>Checking Learning Hub seats…</Typography>
          ) : null}
          {confirmAction === 'approved' && !seatsLoading && selectedBlocked ? (
            <Typography className={classes.blockReason} role="alert">{selectedBlocked}</Typography>
          ) : null}
          {confirmAction === 'approved' && selectedAccess ? (
            <div className={classes.seatBox}>
              {selectedAccess.seat_message ? (
                <Typography>{selectedAccess.seat_message}</Typography>
              ) : null}
              <Typography className={classes.meta}>
                {`Allocated ${summary.allocated ?? '—'} · Utilized ${summary.utilized ?? '—'} · Available ${summary.available ?? '—'}`}
              </Typography>
            </div>
          ) : null}
          <TextField
            label="Comment (optional)"
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            variant="outlined"
            size="small"
            fullWidth
            multiline
            rows={3}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm} disabled={submitting}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={submitReview}
            disabled={submitting || (confirmAction === 'approved' && (seatsLoading || Boolean(selectedBlocked)))}
          >
            {submitting ? <CircularProgress color="inherit" size={16} /> : (confirmAction === 'approved' ? 'Approve' : 'Decline')}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default LearningHubRequests;
